/**
 * Session and server identity.
 *
 * Two facts decide what the whole application renders: who is signed in, and
 * what this server is. Both are established once at start-up, in parallel, and
 * both live under the cache keys in `@/api/queries` so that an admin changing
 * the server's name refreshes the header without anybody wiring it up.
 *
 * Not being signed in is an ordinary state here, not an error. `GET /auth/me`
 * answering 401 is exactly what the login screen expects to happen, so it
 * resolves to `null` rather than throwing into an error boundary.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import { api, isApiError, MFA_ENROLLMENT_EVENT, SESSION_EXPIRED_EVENT } from '@/api/client'
import { defaultQueryRetry, queryKeys } from '@/api/queries'
import type {
  AuthSession,
  CurrentUser,
  LoginRequest,
  MfaChallenge,
  ServerInfo,
} from '@/api/types'
import { isMfaChallenge } from '@/api/types'
import { SESSION_INTENT_KEY, sessionIntent, setSessionIntent } from '@/api/sessionIntent'
import { clearDrafts } from '@/api/drafts'

const MINUTE = 60_000
let sessionWork: Promise<unknown> = Promise.resolve()

function withSessionLifecycle<T>(work: () => Promise<T>): Promise<T> {
  const run = (): Promise<T> => typeof navigator !== 'undefined' && navigator.locks
    ? navigator.locks.request('relaii:auth-lifecycle:v1', { mode: 'exclusive', signal: AbortSignal.timeout(30_000) }, work).then((result) => result)
    : work()
  const pending = sessionWork.then(run, run)
  sessionWork = pending.catch(() => undefined)
  return pending
}

async function revokePendingLogout(): Promise<void> {
  if (sessionIntent() !== 'pending-logout') return
  try {
    await api.post<void>('/auth/logout')
  } catch (error) {
    if (!isApiError(error) || error.status !== 401) throw error
  }
  setSessionIntent('signed-out')
}

/**
 * Shown on the login screen when a session ended by itself. Without it the user
 * is thrown back to a login form mid-sentence with no explanation, which reads
 * as the application losing their work rather than as a session expiring.
 */
export const SESSION_ENDED_MESSAGE =
  'Your session ended. Sign in again to pick up where you left off.'

export interface AuthContextValue {
  /** Null when nobody is signed in. Never null while `loading` is false and a route is protected. */
  user: CurrentUser | null
  /** True until both the session and the server identity have been established. */
  loading: boolean
  /** Startup failed without proving that the saved session has ended. */
  sessionUnavailable: boolean
  /** Null on a server that has not been set up yet, or that could not be reached. */
  server: ServerInfo | null
  /** Set when the session ended on its own; cleared on the next successful sign-in. */
  sessionMessage: string | null
  login(login: string, password: string): Promise<MfaChallenge | void>
  completeMfa(token: string, code: string): Promise<void>
  logout(): Promise<void>
  refresh(): Promise<void>
  dismissSessionMessage(): void
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * A session that does not exist is not a failure. 401 means signed out; 403
 * `setup_required` means the server has not been claimed yet and the setup
 * route will take over. Anything else is a genuine fault and is rethrown.
 */
function isAbsentSession(error: unknown): boolean {
  if (!isApiError(error)) return false
  return error.status === 401 || error.code === 'setup_required'
}

async function fetchCurrentUser(signal: AbortSignal): Promise<CurrentUser | null> {
  if (sessionIntent() !== null) return null
  try {
    return await api.get<CurrentUser>('/auth/me', undefined, signal)
  } catch (error) {
    if (isAbsentSession(error)) return null
    throw error
  }
}

async function fetchServerInfo(signal: AbortSignal): Promise<ServerInfo | null> {
  try {
    return await api.get<ServerInfo>('/server', undefined, signal)
  } catch (error) {
    if (isAbsentSession(error)) return null
    throw error
  }
}

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const queryClient = useQueryClient()
  const [sessionMessage, setSessionMessage] = useState<string | null>(null)
  const [forcedSignedOut, setForcedSignedOut] = useState(() => sessionIntent() !== null)

  // Two hooks in one component start their fetches in the same tick, so the
  // session and the server identity are established in parallel.
  const meQuery = useQuery({
    queryKey: queryKeys.me(),
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    staleTime: MINUTE,
    // A signed-out session already resolved to null above, so only a transport
    // fault or a 5xx can reach this and only those are worth another attempt.
    retry: defaultQueryRetry,
  })

  const serverQuery = useQuery({
    queryKey: queryKeys.server(),
    queryFn: ({ signal }) => fetchServerInfo(signal),
    staleTime: 5 * MINUTE,
    retry: defaultQueryRetry,
  })

  const user = forcedSignedOut ? null : meQuery.data ?? null
  const server = serverQuery.data ?? null

  // Read by the session-expired listener, which must not be re-subscribed on
  // every change of user just to know whether there was one.
  const userRef = useRef<CurrentUser | null>(null)
  useEffect(() => {
    userRef.current = user
  }, [user])

  /**
   * Tears the session down locally. Everything cached was fetched as the
   * departing user and none of it may be shown to the next one.
   */
  const endSession = useCallback(
    (message: string | null): void => {
      api.invalidateSession()
      clearDrafts()
      api.setAccessToken(null)

      // Server identity is a property of the deployment, not of the session, so
      // it is put straight back. Refetching it would blank the login screen for
      // a round trip to learn what it already knows.
      const knownServer = queryClient.getQueryData<ServerInfo | null>(queryKeys.server()) ?? null

      queryClient.clear()
      queryClient.setQueryData<CurrentUser | null>(queryKeys.me(), null)
      queryClient.setQueryData<ServerInfo | null>(queryKeys.server(), knownServer)

      setForcedSignedOut(true)
      setSessionMessage(message)
    },
    [queryClient],
  )

  const establishSession = useCallback(async (): Promise<void> => {
    setSessionIntent(null)
    setForcedSignedOut(false)
    setSessionMessage(null)

    // Nothing cached before a sign-in was fetched as this user.
    queryClient.removeQueries({ queryKey: queryKeys.all, type: 'inactive' })
    await queryClient.refetchQueries({ queryKey: queryKeys.all, type: 'active' })
  }, [queryClient])

  const login = useCallback(
    (identifier: string, password: string): Promise<MfaChallenge | void> => withSessionLifecycle(async () => {
      // Finish the old revocation before opening a new cookie session. Never
      // let a delayed logout accidentally revoke the newly signed-in account.
      await revokePendingLogout()
      const credentials: LoginRequest = { login: identifier, password }

      // The reply may carry tokens for bearer clients. The web client ignores
      // them: adopting one here would switch this tab to bearer auth and with
      // it stop sending the CSRF header the cookie session is protected by.
      // An enrolled account answers with a challenge and no cookies; the
      // session is opened only after `completeMfa`.
      const result = await api.post<AuthSession | MfaChallenge>('/auth/login', credentials)

      if (isMfaChallenge(result)) return result

      await establishSession()
    }),
    [establishSession],
  )

  const completeMfa = useCallback(
    (token: string, code: string): Promise<void> => withSessionLifecycle(async () => {
      await revokePendingLogout()
      await api.post<AuthSession>('/auth/mfa', { mfa_token: token, code })
      await establishSession()
    }),
    [establishSession],
  )

  const logout = useCallback(async (): Promise<void> => {
    setSessionIntent('pending-logout')
    endSession(null)
    // Stop active queries first so a refetch racing the cookie revocation
    // cannot immediately repopulate the departing user's session.
    await queryClient.cancelQueries({ queryKey: queryKeys.all })
    try {
      await withSessionLifecycle(revokePendingLogout)
    } catch {
      // A session the server has already forgotten, or a request that never
      // arrived, still ends the session in this browser. Refusing to sign out
      // because the network is down is the wrong side to fail on.
    }

  }, [endSession, queryClient])

  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== SESSION_INTENT_KEY) return
      if (event.newValue !== sessionIntent()) return
      if (event.newValue !== null) {
        if (userRef.current !== null) endSession(null)
      } else {
        // A different tab established a new identity. Reload tears down every
        // old store/socket before reading the shared HttpOnly cookies again.
        window.location.reload()
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [endSession])

  useEffect(() => {
    const retryLogout = (): void => {
      if (sessionIntent() === 'pending-logout') void withSessionLifecycle(revokePendingLogout).catch(() => undefined)
    }
    retryLogout()
    window.addEventListener('online', retryLogout)
    return () => window.removeEventListener('online', retryLogout)
  }, [])

  const refresh = useCallback(async (): Promise<void> => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: queryKeys.me() }),
      queryClient.refetchQueries({ queryKey: queryKeys.server() }),
    ])
  }, [queryClient])

  const dismissSessionMessage = useCallback((): void => {
    setSessionMessage(null)
  }, [])

  // The refresh token was rejected: the session is gone and cannot be rebuilt
  // from here. Say so, rather than letting the next action fail on its own.
  useEffect(() => {
    const onSessionExpired = (): void => {
      endSession(userRef.current === null ? null : SESSION_ENDED_MESSAGE)
    }

    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired)
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired)
  }, [endSession])

  // An admin turned on `require_mfa` while this tab was already signed in.
  // The next API call answers 403; patching the cached server record is what
  // sends Gate to `/account` without waiting for the five-minute stale time.
  useEffect(() => {
    const onMfaRequired = (): void => {
      queryClient.setQueryData<ServerInfo | null>(queryKeys.server(), (current) => {
        if (current === null || current === undefined) return current
        return { ...current, require_mfa: true }
      })
    }

    window.addEventListener(MFA_ENROLLMENT_EVENT, onMfaRequired)
    return () => window.removeEventListener(MFA_ENROLLMENT_EVENT, onMfaRequired)
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      server,
      loading: meQuery.isPending || serverQuery.isPending,
      sessionUnavailable: !forcedSignedOut && meQuery.isError && meQuery.data === undefined,
      sessionMessage,
      login,
      completeMfa,
      logout,
      refresh,
      dismissSessionMessage,
    }),
    [
      user,
      server,
      meQuery.isPending,
      meQuery.isError,
      meQuery.data,
      forcedSignedOut,
      serverQuery.isPending,
      sessionMessage,
      login,
      completeMfa,
      logout,
      refresh,
      dismissSessionMessage,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)

  if (value === null) {
    throw new Error('useAuth must be used inside <AuthProvider>.')
  }

  return value
}

/**
 * Guards a route. Sends an unauthenticated visitor to the login screen and
 * remembers where they were headed, so signing in resumes the journey instead
 * of dumping them on the home page.
 *
 * `user` is null for the render in which the redirect is scheduled, so callers
 * must still handle it — render nothing, or a spinner while `loading`.
 */
export function useRequireAuth(): AuthContextValue {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const { user, loading, sessionMessage, sessionUnavailable } = auth
  const from = `${location.pathname}${location.search}`

  useEffect(() => {
    if (loading || sessionUnavailable || user !== null) return

    navigate('/login', {
      replace: true,
      state: { from, reason: sessionMessage },
    })
  }, [loading, sessionUnavailable, user, navigate, from, sessionMessage])

  return auth
}

/**
 * True when this session is valid but the operator has required a second
 * factor this account has not confirmed yet. Rooms, admin and the socket
 * refuse until enrolment finishes; `/auth/me` and the MFA endpoints do not.
 */
export function needsMfaEnrollment(
  user: CurrentUser | null,
  server: ServerInfo | null,
): boolean {
  return user !== null && server?.require_mfa === true && user.mfa_enabled !== true
}
